import { forwardRef } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type MaxDistanceSliderInput_query$key } from '@/__generated__/MaxDistanceSliderInput_query.graphql'
import { SliderInput, type SliderInputProps, type SliderInputRef } from '@/components'
import { useHasMetricLocale, useSearchDistanceValues } from '@/hooks'

export type MaxDistanceSliderInputProps = Omit<SliderInputProps, 'ref' | 'sliderProps'> & {
  queryRef: MaxDistanceSliderInput_query$key | null | undefined
}

export const MaxDistanceSliderInput = forwardRef<SliderInputRef, MaxDistanceSliderInputProps>(
  ({ queryRef, ...props }, ref) => {
    const hasMetricLocale = useHasMetricLocale()
    const queryData = useFragment(
      graphql`
        fragment MaxDistanceSliderInput_query on Query {
          currentUser {
            isMetricSystem
          }
        }
      `,
      queryRef
    )
    const { maxValue, minValue, units } = useSearchDistanceValues(
      queryData?.currentUser?.isMetricSystem ?? hasMetricLocale
    )

    return (
      <SliderInput
        accessibilityHint={`Setting max value of ${maxValue} will indicate worldwide (ie: "ALL")`}
        helper={`We may sometimes show you profiles that are outside your search area due to a higher compatibility score. We don’t want a few ${units} to cause you to miss out!`}
        label={`Search Distance (in ${units})`}
        ref={ref}
        sliderProps={{
          minValue,
          maxValue: maxValue + 1
        }}
        withTextInput
        {...props}
      />
    )
  }
)

MaxDistanceSliderInput.displayName = 'MaxDistanceSliderInput'
